import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Dispatch, Fragment, SetStateAction, useCallback } from 'react';
import { PhotoSlider } from 'react-photo-view';
import { Images } from '@/assets/images';
import { RuntimeConfiguration } from '@/configuration';
import { NftSoldDetail } from '@/lib/providers/NftContextProvider';
import { truncateAddress } from '@/lib/utils/common.utils';
import 'react-photo-view/dist/react-photo-view.css';
import { getChainExplorerAddress } from '@/lib/utils/getChainExplorer.utils';
import RenderNFTItem from './RenderNFTItem';

type Props = {
  selectedNft?: NftSoldDetail;
  isMobile: boolean;
  visible: boolean;
  index: number;
  setIndex: Dispatch<SetStateAction<number>>;
  handlePreviewImage: () => void;
};

const NftLeftDetails: React.FC<Props> = ({
  selectedNft: nftDetails,
  isMobile,
  visible,
  index,
  setIndex,
  handlePreviewImage
}) => {
  const theme = useTheme();

  const handleContractAddress = useCallback(() => {
    const contractAddress = nftDetails?.contract_Address;

    if (contractAddress) {
      const url = getChainExplorerAddress(+RuntimeConfiguration.CHAIN_ID, contractAddress);

      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [nftDetails?.contract_Address]);

  const handleTokenId = useCallback(() => {
    const contractAddress = nftDetails?.contract_Address;
    const tokenId = nftDetails?.token_id;

    if (contractAddress && tokenId) {
      const url = `${RuntimeConfiguration.OPEN_SEA_URL}/${contractAddress}/${tokenId}`;

      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [nftDetails?.contract_Address, nftDetails?.token_id]);

  const handleViewMeta = useCallback(() => {
    if (nftDetails?.metadata_url) {
      window.open(nftDetails?.metadata_url, '_blank', 'noopener,noreferrer');
    }
  }, [nftDetails?.metadata_url]);

  return (
    <Fragment>
      {!isMobile && (
        <Stack
          mb={4}
          height={'528px'}
          alignItems="center"
          justifyContent="center"
          bgcolor="#1A1A1A"
          borderRadius="12px"
          position="relative"
          overflow="hidden"
        >
          <PhotoSlider
            images={[{ src: nftDetails?.image || '', key: nftDetails?.image || '' }]}
            visible={visible}
            onClose={handlePreviewImage}
            index={index}
            onIndexChange={setIndex}
          />
          {nftDetails?.animation_url ? (
            <RenderNFTItem item={nftDetails?.animation_url} imageUrl={nftDetails.image} onClick={handlePreviewImage} />
          ) : (
            <RenderNFTItem item={nftDetails?.image || Images.CARD_IMG} onClick={handlePreviewImage} />
          )}
        </Stack>
      )}

      <Box
        sx={{
          mt: '12px',
          borderTop: '1px solid #54585F'
        }}
      >
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon color="primary" />}
            aria-controls="panel3-content"
            id="panel3-header"
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontFamily: theme?.MojitoClaim?.font?.secondary,
              padding: '0'
            }}
          >
            Details
          </AccordionSummary>
          <AccordionDetails sx={{ textAlign: 'left', padding: '0' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" paddingTop={'8px'}>
              <Typography
                color="text.primary"
                sx={{
                  fontFamily: theme?.MojitoClaim?.font?.tertiary,
                  fontWeight: 700,
                  lineHeight: '100%',
                  letterSpacing: '-0.28px',
                  fontSize: '14px',
                  fontStyle: 'normal'
                }}
              >
                Network:
              </Typography>
              <Stack direction="row" alignItems="center" justifyContent="flex-start">
                <Typography fontSize="16px" fontFamily={theme?.MojitoClaim?.font?.tertiary}>
                  {nftDetails?.network}
                </Typography>

                <Typography
                  ml={1}
                  sx={{
                    backgroundColor: '#232323',
                    borderRadius: '12px',
                    p: '4px 8px',
                    fontSize: '12px',
                    lineHeight: '120%',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    fontFamily: theme?.MojitoClaim?.font?.tertiary,
                    color: theme.palette.text.primary,
                    letterSpacing: '-0.24px'
                  }}
                >
                  {nftDetails?.token_type}
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" paddingTop={'8px'}>
              <Typography
                color="text.primary"
                sx={{
                  fontFamily: theme?.MojitoClaim?.font?.tertiary,
                  fontWeight: 700,
                  lineHeight: '100%',
                  letterSpacing: '-0.28px',
                  fontSize: '14px',
                  fontStyle: 'normal'
                }}
              >
                Contract Address:
              </Typography>
              <Typography
                ml={3}
                color="primary"
                onClick={handleContractAddress}
                sx={{
                  fontFamily: theme?.MojitoClaim?.font?.tertiary,
                  fontWeight: 700,
                  lineHeight: '140%',
                  letterSpacing: '-0.14px',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                {truncateAddress(nftDetails?.contract_Address)}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" paddingTop={'8px'}>
              <Typography
                color="text.primary"
                sx={{
                  fontFamily: theme?.MojitoClaim?.font?.tertiary,
                  fontWeight: 700,
                  lineHeight: '100%',
                  letterSpacing: '-0.28px',
                  fontSize: '14px',
                  fontStyle: 'normal'
                }}
              >
                Token ID:
              </Typography>
              <Typography
                ml={3}
                color="primary"
                onClick={handleTokenId}
                sx={{
                  fontFamily: theme?.MojitoClaim?.font?.tertiary,
                  fontWeight: 700,
                  lineHeight: '140%',
                  letterSpacing: '-0.14px',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                {nftDetails?.token_id}
              </Typography>
            </Stack>
          </AccordionDetails>
          <AccordionActions
            sx={{
              mt: '12px',
              padding: '0'
            }}
          >
            <Typography
              ml={3}
              fontSize="16px"
              color="primary"
              sx={{
                fontFamily: theme?.MojitoClaim?.font?.tertiary,
                fontWeight: 400,
                lineHeight: '140%',
                letterSpacing: '-0.14px',
                fontSize: '14px',
                fontStyle: 'normal',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
              onClick={handleViewMeta}
            >
              View Metadata
            </Typography>
          </AccordionActions>
        </Accordion>
      </Box>
      <Box
        mb={3}
        sx={{
          mt: '12px',
          borderTop: '1px solid #54585F'
        }}
      >
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon color="primary" />}
            aria-controls="panel3-content"
            id="panel3-header"
            sx={{ display: 'flex', alignItems: 'center', padding: '0' }}
          >
            Traits
          </AccordionSummary>
          <AccordionDetails sx={{ textAlign: 'left', padding: '0' }}>
            <Grid container spacing={2}>
              {nftDetails?.traits?.map((trait, index) => (
                <Grid item xs={6} sm={4} key={index} overflow="hidden">
                  <Box
                    sx={{
                      border: '1px solid #9E9E9E',
                      backgroundColor: '#232323',
                      borderRadius: '4px',
                      padding: '12px',
                      // minHeight: '68px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Typography
                      color={theme.ClaimTemplate?.collectionHeaderColor}
                      sx={{
                        fontSize: '14px',
                        lineHeight: '140%',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontFamily: theme?.MojitoClaim?.font?.tertiary
                      }}
                    >
                      {trait.trait_type}
                    </Typography>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" paddingTop={'8px'}>
                      <Typography
                        color="text.primary"
                        sx={{
                          fontSize: '16px',
                          lineHeight: '120%',
                          fontStyle: 'normal',
                          fontWeight: 700,
                          fontFamily: theme?.MojitoClaim?.font?.tertiary,
                          letterSpacing: '-0.32px',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden'
                        }}
                      >
                        {trait.value}
                      </Typography>
                      <Typography
                        ml={1}
                        color={theme.ClaimTemplate?.collectionHeaderColor}
                        sx={{
                          fontSize: '14px',
                          lineHeight: '140%',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          fontFamily: theme?.MojitoClaim?.font?.tertiary
                        }}
                      >
                        {trait.percentage}
                      </Typography>
                    </Stack>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Fragment>
  );
};

export default NftLeftDetails;
